import { EntityTaxVM } from './../../../models/entity';
import { IAppState } from './../../state/app.state';
import { getUserState } from './../selectors/user.selectors';
import { map, switchMap, catchError, mergeMap } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AccountVM } from '../../../models/account';
import { UserService } from '../../../services/http/user.service';
import * as NotificationActions from '../../global-notification/actions/global-notification.action';
import * as UserActions from '../actions/user.actions';
import * as GeneralSelectionAction from '../../general-selection/actions/general-selection.actions';
import { IUserState } from './../../state/user.state';
import { AuthService } from 'src/app/services/http/auth.service';
import { ITaxStatus, UserFacade } from 'src/app/store/user/facade/user.facade';
import { IUserAddress } from '../../../models/userAddress';
import { deduplicatingSwitchMap } from 'src/app/utils/deduplicatingSwitchMap';

@Injectable()
export class UserEffects {
    public loggedIn = false;
    public userSubscription: Subscription;
    public userState: IUserState;

    constructor(private actions$: Actions, private userService: UserService, private store: Store<IAppState>, private userFacade: UserFacade) {
        this.userSubscription = this.store.pipe(select(getUserState))
            .subscribe((state: IUserState) => {
                this.userState = state;
            });
    }

    retrieveUserProfile$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.GET_USER_PROFILE),
            deduplicatingSwitchMap((action: UserActions.GetUserProfile) => {
                return this.userService.getUserProfile()
                    .pipe(
                        switchMap((res: any) => {
                            if (res) {
                                let greeting = this.userFacade.getUserGreeting();
                                return of(
                                    new GeneralSelectionAction.UpdateClientTitle(`${greeting} ${res.firstName ? res.firstName : res.email}`),
                                    new UserActions.SetUserProfile(res)
                                );
                            } else {
                                return of(new UserActions.GetUserProfileFail('User not logged in.'));
                            }
                        })
                        , catchError((err) => {
                            const error = (err && err.error_description) ? err.error_description : (err.error.error_description) ? err.error.error_description : 'unknown error';
                            return of(
                                new NotificationActions.NotificationErrorAction(error),
                                new UserActions.GetUserProfileFail(error)
                            );
                        })
                    );
            }),
        ));

    updateUserProfile$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.UPDATE_USER_PROFILE),
            switchMap((action: UserActions.UpdateUserProfile) => {
                return this.userService.updateUserProfile(<AccountVM>action.payload)
                    .pipe(
                        map((res: AccountVM) => {
                            return new UserActions.UpdateUserProfileSuccess({
                                message: 'User profile updated successfully.',
                                userProfile: res
                            });
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.UpdateUserProfileFail(err.message)
                            );
                        })
                    );
            }),
        ));

    updateTFA$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.UPDATE_TFA),
            map((action: UserActions.UpdateTFA): any => {
                // if two factor has been set previously, update user profile after disabling
                if (this.userState.viewProfile.userProfile.twoFactorType !== 0) {
                    return this.userService.updateUserProfile(this.userState.viewProfile.userProfile)
                        .pipe(
                            map((res: any) => {
                                return new UserActions.UpdateUserProfileSuccess(
                                    {
                                        message: 'User profile updated successfully.',
                                        userProfile: res
                                    });
                            })
                            , catchError((err) => {
                                return of(
                                    new UserActions.UpdateUserProfileFail(err.message)
                                );
                            })
                        );
                } else {
                    return of(
                        new UserActions.ResetProfileTFA()
                    );
                }
            }),
        ));

    updateUserProfileTFATypeEmail$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.SAVE_USER_PROFILE_TFA_TYPE_EMAIL),
            switchMap((action: UserActions.SaveUserProfileTFATypeEmail) => {
                return this.userService.updateUserProfile(<AccountVM>action.payload)
                    .pipe(
                        map((res: any) => {
                            return new UserActions.SaveUserProfileTFATypeEmailSuccess();
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.SaveUserProfileTFATypeEmailFail(err.message)
                            );
                        })
                    );
            }),
        ));

    requestSMSVerification$ = createEffect(() => this.actions$
        .pipe(ofType(UserActions.REQUEST_SMS_VERIFICATION),
            switchMap((action: UserActions.RequestSMSVerification) => {
                return this.userService.requestSMSVerification(<string>action.payload.entityId, <string>action.payload.body)
                    .pipe(
                        map((res: any) => {
                            return new UserActions.RequestSMSVerificationSuccess();
                        }),
                        catchError((err) => {
                            return of(
                                new UserActions.RequestSMSVerificationFail(err.error.message)
                            );
                        })
                    );
            })
        ));

    verifySMS$ = createEffect(() => this.actions$
        .pipe(ofType(UserActions.VERIFY_SMS),
            switchMap((action: UserActions.VerifySMS) => {
                return this.userService.verifySMS(<AccountVM>action.payload.userProfile, action.payload.body)
                    .pipe(
                        map((res: any) => {
                            if (!res) {
                                return new UserActions.VerifySMSFail('SMS verification fail');
                            } else {
                                return new UserActions.VerifySMSSuccess(<AccountVM>action.payload.userProfile);
                            }
                        }),
                        catchError((err) => {
                            return of(
                                new UserActions.VerifySMSFail(err.error.message)
                            );
                        })
                    );
            })
        ));

    verifySMSSuccess$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.VERIFY_SMS_SUCCESS),
            switchMap((action: UserActions.VerifySMSSuccess) => {
                return this.userService.updateUserProfile(<AccountVM>action.payload)
                    .pipe(
                        map((res: any) => {
                            return new UserActions.SaveUserProfileTFATypeSMSSuccess();
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.SaveUserProfileTFATypeSMSFail(err.message)
                            );
                        })
                    );
            }),
        ));

    CheckTaxStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.GET_TAX_STATUS),
            deduplicatingSwitchMap((action: UserActions.GetTaxStatus) => {
                return this.userService.getTaxStatus()
                    .pipe(
                        map((res: ITaxStatus) => {
                            return new UserActions.GetTaxStatusSuccess(res);
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.GetTaxStatusFail(err.message)
                            );
                        })
                    );
            }),
        ));

    SetTaxStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.SET_TAX_STATUS),
            switchMap((action: UserActions.SetTaxStatus) => {
                return this.userService.setTaxStatus(<EntityTaxVM>action.payload)
                    .pipe(
                        map((res: ITaxStatus) => {
                            return new UserActions.SetTaxStatusSuccess(res);
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.SetTaxStatusFail(err.message)
                            );
                        })
                    );
            }),
        ));

    GetUserRequiredToSetTwoFactorAuthenticationStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.GET_USER_REQUIRED_TO_SET_TWO_FACTOR_AUTHENTICATION),
            deduplicatingSwitchMap((action: UserActions.GetUserRequiredToSetTwoFActorAuthentication) => {
                return this.userService.getUserRequiredToSetTwoFactorAuthentication()
                    .pipe(
                        map((res: boolean) => {
                            return new UserActions.GetUserRequiredToSetTwoFactorAuthenticationSuccess(res);
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.GetUserRequiredToSetTwoFactorAuthenticationFail(err.message)
                            );
                        })
                    );
            }),
        ));

    GetAddress$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.GET_ADDRESS),
            deduplicatingSwitchMap((action: UserActions.GetAddress) => {
                return this.userService.getAddress(<string>action.payload)
                    .pipe(
                        map((res: any) => {
                            return new UserActions.GetAddressSuccess(res);
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.GetAddressFail(err.message)
                            );
                        })
                    );
            }),
        ));

    SetAddress$ = createEffect(() => this.actions$
        .pipe(
            ofType(UserActions.SET_ADDRESS),
            switchMap((action: UserActions.SetAddress) => {
                return this.userService.setAddress(<string>action.payload.userId, <IUserAddress>action.payload.body)
                    .pipe(
                        map((res: any) => {
                            return new UserActions.SetAddressSuccess(res);
                        })
                        , catchError((err) => {
                            return of(
                                new UserActions.SetAddressFail(err.message)
                            );
                        })
                    );
            }),
        ));
}